import api from './api';

export interface Designer {
  id: string;
  name: string;
}

export interface Location {
  id: string;
  name: string;
  currency?: string; // ISO code, e.g. 'SGD' / 'IDR'
  // Per-store price for this dress, returned by Sequelize on the join row.
  DressLocation?: {
    purchasePrice: number | string;
    rentalPrice: number | string;
  };
}

// Per-store price payload sent when creating/updating a dress.
export interface StorePrice {
  locationId: string;
  purchasePrice: number;
  rentalPrice: number;
}

export interface Category {
  id: string;
  name: string;
  displayName: string;
}

export type TierType = 'new' | 'paid_deposit' | 'exclusive' | 'partner';

export interface Dress {
  id: string;
  name: string;
  description?: string;
  dressCategories?: Category[];
  purchasePrice: number;
  rentalPrice: number;
  designerId: string;
  locations?: Location[]; // Array of locations
  isActive: boolean;
  isPopular: boolean;
  includeInStyleMatcher: boolean;
  imageUrl?: string;
  thumbnailUrl?: string;
  tiers: TierType[];
  launchDate?: string;
  createdAt: string;
  updatedAt: string;
  designer?: Designer;
}

export interface DressListResponse {
  data: Dress[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface CreateDressRequest {
  name: string;
  description?: string;
  categoryIds?: string[];
  purchasePrice?: number; // legacy single price (kept for backward compat)
  rentalPrice?: number;
  storePrices?: StorePrice[]; // per-store prices (authoritative)
  designerId: string;
  locationIds?: string[]; // Array of location IDs
  tiers?: TierType[];
  includeInStyleMatcher?: boolean;
  isActive?: boolean;
  isPopular?: boolean;
  launchDate?: string;
}

export const dressService = {
  // Get dresses with pagination and filters
  async getDresses(params?: {
    page?: number;
    limit?: number;
    category?: string;
    designerId?: string;
    designerIds?: string;
    search?: string;
  }): Promise<DressListResponse> {
    const response = await api.get('/dresses', { params });
    return response.data;
  },

  // Get single dress
  async getDress(id: string): Promise<Dress> {
    const response = await api.get(`/dresses/${id}`);
    return response.data.data;
  },

  // Create dress
  async createDress(dress: CreateDressRequest): Promise<Dress> {
    const response = await api.post('/dresses', dress);
    return response.data.data;
  },

  // Update dress
  async updateDress(id: string, updates: Partial<CreateDressRequest>): Promise<Dress> {
    const response = await api.put(`/dresses/${id}`, updates);
    return response.data.data;
  },

  // Delete dress (soft delete)
  async deleteDress(id: string): Promise<void> {
    await api.delete(`/dresses/${id}`);
  },

  // Update style matcher status
  async updateStyleMatcherStatus(id: string, includeInStyleMatcher: boolean): Promise<Dress> {
    const response = await api.put(`/dresses/${id}/style-matcher`, { includeInStyleMatcher });
    return response.data.data;
  },

  // Upload dress image
  async uploadImage(id: string, file: File): Promise<Dress> {
    const formData = new FormData();
    formData.append('image', file);
    const response = await api.post(`/dresses/${id}/image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.data;
  },

  // Delete dress image
  async deleteImage(id: string): Promise<void> {
    await api.delete(`/dresses/${id}/image`);
  },

  // Delete dress image (kept for backward compatibility)
  async deleteDressImage(id: string): Promise<void> {
    await api.delete(`/dresses/${id}/image`);
  },

  // Get dress statistics with date filtering
  async getDressStatistics(params?: {
    startDate?: string;
    endDate?: string;
    dressIds?: string[];
  }): Promise<DressStatisticsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.dressIds && params.dressIds.length > 0) {
      queryParams.append('dressIds', params.dressIds.join(','));
    }
    
    const response = await api.get(`/dresses/statistics?${queryParams}`);
    return response.data;
  },
};

export interface DressStatistics {
  id: string;
  name: string;
  designer: string | null;
  catalogLikes: number;
  styleMatcherLikes: number;
  totalLikes: number;
}

export interface DressStatisticsResponse {
  data: DressStatistics[];
  dateRange: {
    startDate: string | null;
    endDate: string | null;
  };
}